import _ from "lodash";
import { toast } from "react-toastify";

import ReelStore from "./ReelStore";
import { decorate, observable, computed } from "mobx";

// The progress screen walks through a fixed sequence of steps: create the clip, then
// post it to each selected Forge destination in turn. We surface a determinate
// percentage so the linear bar advances one step at a time.
const CREATE_CLIP_STEPS = 1;
const MIN_PROGRESS_PERCENT = 5;

class QuickerClipStore extends ReelStore {
  isCreatingForgeClip = false;

  constructor(quickerClipForgeStore, ...parentArgs) {
    super(...parentArgs);

    this.quickerClipForgeStore = quickerClipForgeStore;
  }

  addClipMetadata(video) {
    const forgeVideoPost = this.quickerClipForgeStore.forgeVideoPost;
    if (forgeVideoPost) {
      forgeVideoPost.durationInSeconds = video.duration;
    }
  }

  updateTitle(title) {
    this.setClipTitle(title);
    this.quickerClipForgeStore.setForgeTitle(title);
  }

  async openDialog(play) {
    await this.initForgeClip(play);
    this.setDialogOpen(true);
  }

  closeDialog() {
    this.setDialogOpen(false);
    this.quickerClipForgeStore.reset();
    this.clear();
  }

  async initForgeClip(play) {
    // If for some reason the play is not defined or null pop a toast and prevent
    // an error from surfacing.
    if (!play) {
      toast.error("Error loading play", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    if (play.get("feedType") === "CMS") {
      this.addVideo({
        url: play.get("videoSrc"),
        thumbnailUri: this.getThumbnail(play)
      });
    } else {
      this.addPlay(play.get("id"));
    }

    const selectedPlay = this.reelClips[0];
    const defaultDescription = play.has("description")
      ? play.get("description")
      : "";

    const forgeClip = {
      name: defaultDescription,
      url: selectedPlay.url,
      thumbnailUri: selectedPlay.thumbnailUri,
      playIds: [selectedPlay.id],
      durationInSeconds: 0
    };

    const commonSearchMetaData = this.getCommonClipSearchMetadata([
      selectedPlay
    ]);

    await this.quickerClipForgeStore.initFields(
      forgeClip,
      commonSearchMetaData
    );
  }

  getThumbnail(play) {
    const feeds = play.get("videos");
    const selectedFeedType = play.get("feedType");
    if (feeds.length && selectedFeedType) {
      const currentFeed = feeds.find(
        feed => feed.feedType === selectedFeedType
      );
      const thumbnail = currentFeed.image;
      if (thumbnail && thumbnail.cuts.length) {
        return thumbnail.cuts[0].at2x;
      }
    }
  }

  get forgeProgressPercent() {
    if (!this.isCreatingForgeClip) {
      return 0;
    }
    const forge = this.quickerClipForgeStore;
    const totalDestinations =
      forge.destinationsBeingSent.length ||
      forge.selectedDestinationNames.length ||
      1;
    const totalSteps = CREATE_CLIP_STEPS + totalDestinations;

    let completedSteps = 0;
    if (!this.isCreatingClip && forge.sendingDestination) {
      completedSteps =
        CREATE_CLIP_STEPS +
        forge.destinationsBeingSent.indexOf(forge.sendingDestination);
    }

    const percent = Math.round((completedSteps / totalSteps) * 100);
    return Math.max(MIN_PROGRESS_PERCENT, percent);
  }

  async createAndSendClip() {
    this.isCreatingForgeClip = true;
    const response = await this.createClip();

    if (response && response.id != null) {
      this.quickerClipForgeStore.clipsToSearch = this.myClipsStore.videos;
      await this.quickerClipForgeStore.sendClipToForge(response.id);
    } else {
      toast.error("Failed to create clip", {
        position: "top-right",
        autoClose: 3000
      });
    }

    this.isCreatingForgeClip = false;
    this.closeDialog();
  }

  async createClip() {
    if (!this.validateReel()) {
      return;
    }

    const clips = this.reelClips;
    this.isCreatingClip = true;
    return this.clippyApi
      .createUserClip(
        clips,
        this.applyCrossfade,
        true,
        this.cropRatio,
        this.watermark
      )
      .then(async resp => {
        const clipResp = resp
          ? _.find(resp, clip => clip.clipOutputType === "MP4")
          : {};
        const {
          durationInSeconds,
          id,
          uri,
          thumbnails,
          width,
          height
        } = clipResp;
        const time = this._getCurrentTime();
        const name =
          this.clipTitle === "" ? ` Untitled: ${time}` : this.clipTitle;
        const thumbnailUri =
          thumbnails && thumbnails.length > 0 ? thumbnails[0].uri : "";
        const commonClipSearchMetadata = this.getCommonClipSearchMetadata(
          clips
        );

        const putUserClipResponse = await this.sportyApi.putUserClip({
          title: name,
          id: id,
          clips: clips,
          addWatermark: this.addWatermark,
          applyCrossfade: this.applyCrossfade,
          cropRatio: this.cropRatio,
          videoUri: uri,
          thumbnailUri,
          width,
          height,
          duration: durationInSeconds,
          searchCategory: commonClipSearchMetadata.category,
          username: this.authStore.user.preferred_username
        });

        await this.myClipsStore.refreshClips();

        return putUserClipResponse;
      })
      .catch(err => {
        console.log(err);
        toast.error("Failed to create clip. Please try again.", {
          position: "top-right",
          autoClose: 3000
        });
      })
      .finally(() => {
        this.isCreatingClip = false;
      });
  }
}

decorate(QuickerClipStore, {
  isCreatingForgeClip: observable,
  forgeProgressPercent: computed
});

export default QuickerClipStore;



// WEBPACK FOOTER //
// ./src/stores/QuickerClipStore.js